<script setup>

import {nextTick} from 'vue';

import SearchPill from "@/components/common/other/SearchPill";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown";
import StaticTooltip from "@/components/common/other/StaticTooltip.vue";
import DateInput from "@/components/common/input/DateInput.vue"

import API from "@/classes/global/API";

import {useCheckSearchCriteriaExists} from "@/composables/common/useCheckSearchCriteriaExists";
import {useCheckFieldsForData} from "@/composables/common/useCheckFieldsForData";

import DropDownItem from "@/classes/component-classes/DropDownItem";

const emits = defineEmits(['search', 'clear', 'clearAll']);

const props = defineProps({

  modelValue: {}

})

function handleSearch(searchPill) {

  nextTick(async () => {

    props.modelValue.isSearchData = useCheckSearchCriteriaExists(props.modelValue.searchCriteria);

    if (props.modelValue.isSearchData) {

      emits('search');

      const response = await API.searchSkuHistory(props.modelValue.searchCriteria);

      if (response) {

        props.modelValue.skuNames = response;
        props.modelValue.skuComposites = [];
        props.modelValue.skuCompositeGridData = [];

        let searchHistoryItemExists;

        if (props.modelValue.searchCriteria[searchPill]) {

          searchHistoryItemExists = props.modelValue.searchPills[searchPill]
              .history?.find((item) => item.value === props.modelValue.searchCriteria[searchPill]);

          if (!searchHistoryItemExists && response.length > 0) props.modelValue.searchPills[searchPill].history
              ?.unshift(new DropDownItem(props.modelValue.searchPills[searchPill].history.length + 1,
                  props.modelValue.searchCriteria[searchPill]));
        }

        if (response.length === 0) props.modelValue.isSearchData = false;
      }

    }

  })
}

function handleClearSearch(searchPill) {

  // Move the events along to ensure clearing of the box has happened before data present check
  nextTick(() => {

    if (!useCheckFieldsForData(props.modelValue.searchCriteria)) {
      handleClearAllFields();

    } else {

      handleSearch(searchPill);
    }

  })

}

function handleClearAllFields(clearAll) {

  if(clearAll) return emits('clearAll');

  emits('clear');

}

</script>

<template>

  <div class="cmp-sku-history-search-pill-group_search-area">
    <div class="cmp-sku-history-search-pill-group_search-pill-container">
      <SearchPill
          @search="handleSearch('skuGroup')"
          :enabled="props.modelValue.searchPills.skuGroup.enabled">
        <template #control>
          <MultiSearchDropDown
              :key="props.modelValue.skuGroups.length"
              :width="240"
              :maxLength="50"
              placeholder="Search or Select"
              :allowCustomValue="false"
              :disableFilter="false"
              searchType="iata"
              label="SKU Group"
              :label-top="true"
              @clear="handleClearSearch('skuGroup')"
              @select="handleSearch('skuGroup')"
              @keydown.enter="handleSearch('skuGroup')"
              :dataSource="props.modelValue.skuGroups"
              v-model="props.modelValue.searchCriteria.skuGroup"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('skuNumber')"
          :enabled="props.modelValue.searchPills.skuName.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="230"
              :maxLength="50"
              placeholder="Enter Search Criteria..."
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="SKU Number"
              :label-top="true"
              @clear="handleClearSearch('skuNumber')"
              @select="handleSearch('skuNumber')"
              @keydown.enter="handleSearch('skuNumber')"
              :dataSource="props.modelValue.searchPills.skuNumber.history"
              v-model="props.modelValue.searchCriteria.skuNumber"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('skuName')"
          :enabled="props.modelValue.searchPills.skuName.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="230"
              :maxLength="50"
              placeholder="Enter Search Criteria..."
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="SKU Name"
              :label-top="true"
              @clear="handleClearSearch('skuName')"
              @select="handleSearch('skuName')"
              @keydown.enter="handleSearch('skuName')"
              :dataSource="props.modelValue.searchPills.skuName.history"
              v-model="props.modelValue.searchCriteria.skuName"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('shipmentNumber')"
          :enabled="props.modelValue.searchPills.skuName.enabled">
        <template #control>
          <MultiSearchDropDown
              :width="230"
              :maxLength="50"
              placeholder="Enter Search Criteria..."
              :allowCustomValue="true"
              :disableFilter="false"
              searchType="iata"
              label="Shipment Number"
              :label-top="true"
              @clear="handleClearSearch('shipmentNumber')"
              @select="handleSearch('shipmentNumber')"
              @keydown.enter="handleSearch('shipmentNumber')"
              :dataSource="props.modelValue.searchPills.jobNumber.history"
              v-model="props.modelValue.searchCriteria.jobNumber"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('dateFrom')"
          :enabled="props.modelValue.searchPills.dateFrom.enabled">
        <template #control>
          <DateInput
              @clear="handleClearSearch('dateFrom')"
              :width="140"
              :label-top="true"
              :label-width="128"
              v-model="props.modelValue.searchCriteria.dateFrom"
              label="Date From">
            <el-date-picker
                :clearable="false"
                style="margin-right: 10px"
                v-model="props.modelValue.searchCriteria.dateFrom"
                value-format="DD-MMM-YY"/>
          </DateInput>
        </template>
      </SearchPill>
      <SearchPill
          @search="handleSearch('dateTo')"
          :enabled="props.modelValue.searchPills.dateFrom.enabled">
        <template #control>
          <DateInput
              @clear="handleClearSearch('dateTo')"
              :width="140"
              :label-top="true"
              :label-width="128"
              v-model="props.modelValue.searchCriteria.dateTo"
              label="Date To">
            <el-date-picker
                :clearable="false"
                style="margin-right: 10px"
                v-model="props.modelValue.searchCriteria.dateTo"
                value-format="DD-MMM-YY"/>
          </DateInput>
        </template>
      </SearchPill>
      <div class="cmp-search-stock-pill-group_clear-button">
        <StaticTooltip :icon="false">
          <template #content>
            Clear All
          </template>
          <i class="fa-solid fa-trash-can-list" style="color:#4c4c4c; cursor: pointer;"
             @click="handleClearAllFields(true)"></i>
        </StaticTooltip>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cmp-sku-history-search-pill-group {

  &_search-area {
    display: flex;
    align-items: center;
    padding-left: 10px;
    height: 90px;
    //margin-left: 10px;
    background-color: #ffffff;
    border-radius: var(--rounded-corners);
    border: 1px solid var(--border-color);
    border-left: 6px solid var(--left-border-highlight);
  }

  &_search-pill-container {
    display: flex;
    align-items: center;
    height: 80px;
    gap: 20px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0 5px 0 5px;
  }

  &_clear-button {
    display: flex;
    align-items: center;
    margin-left: 30px;
  }

}

</style>
